import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Boxes, Plus, Search, Upload } from "lucide-react";
import { Badge, Button, EmptyState, Select, Stack, Text, TextInput } from "@ux/primitives";
import { BottomActionBar, ConfirmAction, SplitPane, Toolbar } from "@ux/compositions";
import { useViewport } from "@ux/hooks";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { useApiClient } from "@shell/apiClientContext";
import { BulkActionBar } from "../common/BulkActionBar";
import { describeApiError } from "../common/errors";
import { ExportDataButton } from "../common/ExportDataButton";
import { buildAssetsExportCsv } from "../common/exportCsv";
import { FLOORPLAN_QUERY_KEY } from "../common/queryKeys";
import { runBulk, summarizeBulkFailures } from "../common/runBulk";
import {
    ASSET_ASSIGNMENTS_QUERY_KEY,
    useCreateAssetAssignment,
    useReleaseAssetAssignment,
} from "../asset-assignments/hooks";
import { createAssetAssignment, exportAssetAssignments, releaseAssetAssignment } from "../asset-assignments/api";
import { useSageOffices } from "../sage-offices/hooks";
import { useAddAssetToUser } from "../users/hooks";
import { useAddAssetToWorkspace } from "../workspaces/hooks";
import { AssetsList } from "./AssetsList";
import { AssetForm, type AssetFormMode } from "./AssetForm";
import { AssignTargetDialog } from "./AssignTargetDialog";
import { assetTypes, type AssetAssignTarget, type AssetUpsertPayload, type AssetView } from "./types";
import {
    ASSETS_QUERY_KEY,
    useAssets,
    useCreateAsset,
    useDeleteAsset,
    useRestoreAsset,
    useUpdateAsset,
} from "./hooks";
import { deleteAsset, exportAssets } from "./api";

type Mode = { kind: "idle" } | { kind: "create" } | { kind: "edit"; id: string };

export function AssetsPage(): ReactElement {
    const navigate = useNavigate();
    const { isPhone } = useViewport();
    const canEdit = useCanEdit();
    const client = useApiClient();
    const [search, setSearch] = useState("");
    const [officeFilter, setOfficeFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [mode, setMode] = useState<Mode>({ kind: "idle" });
    const [pendingDelete, setPendingDelete] = useState<string | null>(null);
    const [pendingRestore, setPendingRestore] = useState<string | null>(null);
    const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(() => new Set());
    const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
    const [bulkIntent, setBulkIntent] = useState<null | "delete" | "available">(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkError, setBulkError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    // Office directory: drives the toolbar filter dropdown AND the row
    // renderer (so AssetsList can show the office name instead of the
    // raw FK). One fetch, two consumers. Include deleted so a soft-
    // deleted office still renders a label on historical rows.
    const officesQuery = useSageOffices({ q: "", includeDeleted: true });
    const offices = useMemo(() => officesQuery.data ?? [], [officesQuery.data]);
    const officeIndex = useMemo(() => {
        const map = new Map<string, (typeof offices)[number]>();
        for (const o of offices) map.set(o.id, o);
        return map;
    }, [offices]);

    const assetsQuery = useAssets({
        q: search.trim(),
        sageOfficeId: officeFilter,
        type: typeFilter,
        includeDeleted,
    });
    const createMutation = useCreateAsset();
    const updateMutation = useUpdateAsset();
    const deleteMutation = useDeleteAsset();
    const restoreMutation = useRestoreAsset();
    // Inline-create with assignment: when the form's optional "Assign to"
    // section has a target picked, the save fires whichever atomic
    // endpoint matches the picked kind instead of the regular create.
    const addAssetToWorkspaceMutation = useAddAssetToWorkspace();
    const addAssetToUserMutation = useAddAssetToUser();
    // Edit-mode assignment management: Assign / Re-assign / Release on
    // the asset detail panel route through the same shared mutations as
    // the standalone Asset Assignments page (release-then-create for
    // the re-assign flow — see WorkspacesPage.reassignAsset for the
    // failure-mode rationale; the answer doesn't change here).
    const createAssignmentMutation = useCreateAssetAssignment();
    const releaseAssignmentMutation = useReleaseAssetAssignment();

    const rows = useMemo(() => assetsQuery.data ?? [], [assetsQuery.data]);

    useEffect(() => {
        setSelectedKeys((prev) => {
            if (prev.size === 0) return prev;
            const ids = new Set(rows.map((row) => row.id));
            let changed = false;
            const next = new Set<string>();
            for (const id of prev) {
                if (ids.has(id)) next.add(id);
                else changed = true;
            }
            return changed ? next : prev;
        });
    }, [rows]);

    const selectedAssets = useMemo(
        () => rows.filter((row) => selectedKeys.has(row.id) && !row.isDeleted),
        [rows, selectedKeys],
    );

    const selectedRecord = useMemo(() => {
        if (mode.kind !== "edit") return null;
        return rows.find((r) => r.id === mode.id) ?? null;
    }, [mode, rows]);

    const formMode: AssetFormMode | null = useMemo(() => {
        if (mode.kind === "create") return { kind: "create" };
        if (mode.kind === "edit" && selectedRecord !== null)
            return { kind: "edit", record: selectedRecord };
        return null;
    }, [mode, selectedRecord]);

    const isSaving =
        createMutation.isPending ||
        updateMutation.isPending ||
        addAssetToWorkspaceMutation.isPending ||
        addAssetToUserMutation.isPending;
    const formError =
        mode.kind === "create" && createMutation.isError
            ? describeApiError(createMutation.error)
            : mode.kind === "create" && addAssetToWorkspaceMutation.isError
              ? describeApiError(addAssetToWorkspaceMutation.error)
              : mode.kind === "create" && addAssetToUserMutation.isError
                ? describeApiError(addAssetToUserMutation.error)
                : mode.kind === "edit" && updateMutation.isError
                  ? describeApiError(updateMutation.error)
                  : null;

    const isAssignmentBusy =
        createAssignmentMutation.isPending || releaseAssignmentMutation.isPending;
    const assignmentErrorMessage = createAssignmentMutation.isError
        ? describeApiError(createAssignmentMutation.error)
        : releaseAssignmentMutation.isError
          ? describeApiError(releaseAssignmentMutation.error)
          : null;

    function handleSave(payload: AssetUpsertPayload): void {
        // Every successful mutation returns the user to the list (idle). The
        // alternative — landing on the freshly-saved record's edit form —
        // strands the user there: they have to click "Cancel" to get back
        // to the list before they can do anything else. Idle drops them
        // back at their natural next step (pick another, or close).
        if (mode.kind === "create") {
            const assignTo = payload.assignTo ?? null;
            if (assignTo !== null && assignTo.kind === "Workspace") {
                addAssetToWorkspaceMutation.mutate(
                    {
                        workspaceId: assignTo.id,
                        payload: {
                            asset: assetCorePayload(payload),
                            assignmentId: crypto.randomUUID(),
                        },
                    },
                    { onSuccess: () => setMode({ kind: "idle" }) },
                );
            } else if (assignTo !== null && assignTo.kind === "User") {
                addAssetToUserMutation.mutate(
                    {
                        userId: assignTo.id,
                        payload: {
                            asset: assetCorePayload(payload),
                            assignmentId: crypto.randomUUID(),
                        },
                    },
                    { onSuccess: () => setMode({ kind: "idle" }) },
                );
            } else {
                createMutation.mutate(payload, {
                    onSuccess: () => setMode({ kind: "idle" }),
                });
            }
        } else if (mode.kind === "edit") {
            updateMutation.mutate(payload, {
                onSuccess: () => setMode({ kind: "idle" }),
            });
        }
    }

    function handleAssignNew(target: AssetAssignTarget): void {
        if (mode.kind !== "edit") return;
        const assetId = mode.id;
        createAssignmentMutation.mutate(buildAssignmentPayload(assetId, target));
    }

    function handleReassign(currentAssignmentId: string, target: AssetAssignTarget): void {
        if (mode.kind !== "edit") return;
        const assetId = mode.id;
        // Sequential release-then-create. Same shape used by
        // WorkspacesPage and InventoryPage for desk re-assignments —
        // failure mode there is documented in those files; it's the same
        // here. Briefly, if release succeeds and create fails, the asset
        // is left unassigned (visible state, retryable, no corruption).
        releaseAssignmentMutation.mutate(
            { assetId, assignmentId: currentAssignmentId },
            {
                onSuccess: () => {
                    createAssignmentMutation.mutate(buildAssignmentPayload(assetId, target));
                },
            },
        );
    }

    function handleReleaseAssignment(currentAssignmentId: string): void {
        if (mode.kind !== "edit") return;
        const assetId = mode.id;
        releaseAssignmentMutation.mutate({ assetId, assignmentId: currentAssignmentId });
    }

    async function refreshAfterBulk(): Promise<void> {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ASSETS_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: ASSET_ASSIGNMENTS_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: FLOORPLAN_QUERY_KEY }),
        ]);
    }

    async function handleBulkAssign(target: AssetAssignTarget): Promise<void> {
        setBulkAssignOpen(false);
        const targets = selectedAssets;
        if (targets.length === 0) {
            setBulkError("Deleted assets are skipped. Select active assets to assign.");
            return;
        }
        setBulkBusy(true);
        setBulkError(null);
        const failures = await runBulk(
            targets,
            async (asset) => {
                const currentId = asset.currentAssignmentId;
                if (currentId !== null && currentId !== undefined && currentId !== "") {
                    await releaseAssetAssignment(client, asset.id, currentId);
                }
                await createAssetAssignment(client, buildAssignmentPayload(asset.id, target));
            },
            assetLabel,
        );
        await refreshAfterBulk();
        setBulkBusy(false);
        setBulkError(summarizeBulkFailures(failures));
        if (failures.length === 0) setSelectedKeys(new Set());
    }

    async function handleBulkAvailable(): Promise<void> {
        const targets = selectedAssets.filter((asset) => {
            const currentId = asset.currentAssignmentId;
            return currentId !== null && currentId !== undefined && currentId !== "";
        });
        if (targets.length === 0) {
            setBulkIntent(null);
            setBulkError("Those assets are already available.");
            return;
        }
        setBulkBusy(true);
        setBulkError(null);
        const failures = await runBulk(
            targets,
            async (asset) => {
                const currentId = asset.currentAssignmentId;
                if (currentId === null || currentId === undefined || currentId === "") return;
                await releaseAssetAssignment(client, asset.id, currentId);
            },
            assetLabel,
        );
        await refreshAfterBulk();
        setBulkBusy(false);
        setBulkIntent(null);
        setBulkError(summarizeBulkFailures(failures));
        if (failures.length === 0) setSelectedKeys(new Set());
    }

    async function handleBulkDelete(): Promise<void> {
        const targets = selectedAssets;
        if (targets.length === 0) {
            setBulkIntent(null);
            setBulkError("Deleted assets are already removed from the active list.");
            return;
        }
        setBulkBusy(true);
        setBulkError(null);
        const failures = await runBulk(
            targets,
            async (asset) => {
                await deleteAsset(client, asset.id);
            },
            assetLabel,
        );
        await refreshAfterBulk();
        setBulkBusy(false);
        setBulkIntent(null);
        setBulkError(summarizeBulkFailures(failures));
        if (failures.length === 0) setSelectedKeys(new Set());
    }

    return (
        <Stack direction="column" gap={0} grow style={{ overflow: "hidden" }}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center">
                        <Text role="heading">Assets</Text>
                        <Badge tone="neutral">{rows.length}</Badge>
                    </Stack>
                }
                trailing={
                    <Stack direction="row" gap={2} align="center" wrap>
                        <Search size={14} style={{ opacity: 0.55 }} />
                        <div style={{ width: 220 }}>
                            <TextInput
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Tag, serial, make…"
                            />
                        </div>
                        <div style={{ width: 180 }}>
                            <Select
                                value={officeFilter}
                                onChange={(e) => setOfficeFilter(e.target.value)}
                                options={[
                                    { value: "", label: "All offices" },
                                    ...offices
                                        .filter((o) => !o.isDeleted)
                                        .map((o) => ({ value: o.id, label: o.name })),
                                ]}
                            />
                        </div>
                        <div style={{ width: 140 }}>
                            <Select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value)}
                                options={[
                                    { value: "", label: "All types" },
                                    ...assetTypes.map((t) => ({ value: t, label: t })),
                                ]}
                            />
                        </div>
                        <Button
                            variant={includeDeleted ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => setIncludeDeleted((v) => !v)}
                        >
                            {includeDeleted ? "Showing deleted" : "Show deleted"}
                        </Button>
                        <ExportDataButton
                            filenamePrefix="assets"
                            run={async () => {
                                const [assets, assignments] = await Promise.all([
                                    exportAssets(client, {
                                        q: search.trim(),
                                        sageOfficeId: officeFilter,
                                        type: typeFilter,
                                        includeDeleted,
                                    }),
                                    exportAssetAssignments(client, { open: true }),
                                ]);
                                return {
                                    csv: buildAssetsExportCsv(assets.results, assignments.results),
                                    truncated: assets.truncated || assignments.truncated,
                                    rowCount: assets.results.length,
                                };
                            }}
                        />
                        {/* Bulk import lives next to "New asset" because
                            it's the same conceptual action — adding new
                            rows — at a different scale. Hidden on phone:
                            the import flow needs a desktop-class viewport
                            for the preview table, and the operator who
                            uploads CSVs is unlikely to do it from a
                            handset. */}
                        {!isPhone && canEdit ? (
                            <Button
                                variant="ghost"
                                size="sm"
                                leadingIcon={<Upload size={14} />}
                                onClick={() => navigate("/admin/assets/bulk-import")}
                            >
                                Bulk import
                            </Button>
                        ) : null}
                        {/* Primary "+ New asset" CTA hoists to the BottomActionBar
                            on phone so it lands in the thumb zone instead of the
                            cramped top toolbar. Desktop and tablet keep it inline. */}
                        {!isPhone && canEdit ? (
                            <Button
                                variant="primary"
                                size="sm"
                                leadingIcon={<Plus size={16} />}
                                onClick={() => setMode({ kind: "create" })}
                            >
                                New asset
                            </Button>
                        ) : null}
                    </Stack>
                }
            />

            {canEdit ? (
                <BulkActionBar
                    count={selectedKeys.size}
                    busy={bulkBusy}
                    error={bulkError}
                    onAssign={() => setBulkAssignOpen(true)}
                    onAvailable={() => setBulkIntent("available")}
                    onDelete={() => setBulkIntent("delete")}
                    onClear={() => {
                        setSelectedKeys(new Set());
                        setBulkError(null);
                    }}
                />
            ) : null}

            <SplitPane
                activeBelowDesktop={mode.kind === "idle" ? "master" : "detail"}
                master={
                    <AssetsList
                        rows={rows}
                        officeIndex={officeIndex}
                        selectedId={mode.kind === "edit" ? mode.id : null}
                        onSelect={(row) => setMode({ kind: "edit", id: row.id })}
                        isLoading={assetsQuery.isPending}
                        error={assetsQuery.isError ? describeApiError(assetsQuery.error) : null}
                        {...(canEdit
                            ? { selection: { selectedKeys, onChange: setSelectedKeys } }
                            : {})}
                    />
                }
                detail={
                    formMode === null ? (
                        <EmptyState
                            icon={<Boxes size={32} />}
                            title={canEdit ? "Select an asset to edit" : "Select an asset to view"}
                            description={
                                canEdit
                                    ? "Pick a row on the left, or create a new asset."
                                    : "Pick a row on the left."
                            }
                            action={
                                canEdit ? (
                                    <Button
                                        variant="primary"
                                        leadingIcon={<Plus size={16} />}
                                        onClick={() => setMode({ kind: "create" })}
                                    >
                                        New asset
                                    </Button>
                                ) : undefined
                            }
                        />
                    ) : (
                        <AssetForm
                            mode={formMode}
                            onSave={handleSave}
                            onCancel={() => setMode({ kind: "idle" })}
                            onRequestDelete={(id) => setPendingDelete(id)}
                            onRequestRestore={(id) => setPendingRestore(id)}
                            isSaving={isSaving}
                            errorMessage={formError}
                            onAssignNew={handleAssignNew}
                            onReassign={handleReassign}
                            onReleaseAssignment={handleReleaseAssignment}
                            isAssignmentBusy={isAssignmentBusy}
                            assignmentErrorMessage={assignmentErrorMessage}
                        />
                    )
                }
            />

            {/* Phone-only sticky CTA: replaces the toolbar's primary action
                so the most-used button lives in the thumb zone. Hidden when
                a form is active — the form renders its own action bar. */}
            {isPhone && canEdit && mode.kind === "idle" ? (
                <BottomActionBar>
                    <Button
                        variant="primary"
                        leadingIcon={<Plus size={16} />}
                        onClick={() => setMode({ kind: "create" })}
                    >
                        New asset
                    </Button>
                </BottomActionBar>
            ) : null}

            <AssignTargetDialog
                open={bulkAssignOpen}
                mode={{ kind: "assign" }}
                defaultKind="Workspace"
                assetLabel={
                    selectedAssets.length === 1 ? "1 asset" : `${selectedAssets.length} assets`
                }
                onSubmit={(target) => {
                    void handleBulkAssign(target);
                }}
                onCancel={() => setBulkAssignOpen(false)}
                isWorking={bulkBusy}
                errorMessage={null}
            />
            <ConfirmAction
                open={bulkIntent === "available"}
                title="Make available?"
                description="Releases the open assignment on each selected asset so it stays in the catalog and shows as Available. Assets that are already available are left as they are."
                confirmLabel="Available"
                busy={bulkBusy}
                onCancel={() => setBulkIntent(null)}
                onConfirm={() => {
                    void handleBulkAvailable();
                }}
            />
            <ConfirmAction
                open={bulkIntent === "delete"}
                title="Delete selected assets?"
                description="Marks each selected asset deleted. An asset with an open assignment has to be made Available first."
                confirmLabel="Delete"
                destructive
                busy={bulkBusy}
                onCancel={() => setBulkIntent(null)}
                onConfirm={() => {
                    void handleBulkDelete();
                }}
            />
            <ConfirmAction
                open={pendingDelete !== null}
                title="Delete asset?"
                description="The record will be marked deleted. Any open assignment must be released first."
                confirmLabel="Delete"
                destructive
                busy={deleteMutation.isPending}
                onCancel={() => setPendingDelete(null)}
                onConfirm={() => {
                    if (pendingDelete === null) return;
                    const id = pendingDelete;
                    deleteMutation.mutate(id, {
                        onSuccess: () => {
                            setPendingDelete(null);
                            setMode({ kind: "idle" });
                        },
                    });
                }}
            />
            <ConfirmAction
                open={pendingRestore !== null}
                title="Restore asset?"
                description="The asset will become active again. Its serial number and tag must still be unique."
                confirmLabel="Restore"
                busy={restoreMutation.isPending}
                onCancel={() => setPendingRestore(null)}
                onConfirm={() => {
                    if (pendingRestore === null) return;
                    restoreMutation.mutate(pendingRestore, {
                        onSuccess: () => {
                            setPendingRestore(null);
                            setMode({ kind: "idle" });
                        },
                    });
                }}
            />
        </Stack>
    );
}

/**
 * Strip the AssetUpsertPayload down to the core asset fields the atomic
 * "add asset to {workspace,user}" endpoints expect. Filters out the
 * `assignTo` discriminator, which is the page's own routing hint and
 * has no business in the request body.
 */
function assetCorePayload(payload: AssetUpsertPayload): {
    readonly id: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly make: string;
    readonly model: string;
    readonly serialNumber: string;
    readonly assetTag: string | null;
    readonly purchaseDate: string | null;
    readonly purchasePrice: number | null;
} {
    return {
        id: payload.id,
        sageOfficeId: payload.sageOfficeId,
        type: payload.type,
        make: payload.make,
        model: payload.model,
        serialNumber: payload.serialNumber,
        assetTag: payload.assetTag,
        purchaseDate: payload.purchaseDate,
        purchasePrice: payload.purchasePrice,
    };
}

function assetLabel(asset: AssetView): string {
    if (asset.assetTag !== null && asset.assetTag !== undefined && asset.assetTag !== "") {
        return asset.assetTag;
    }
    return asset.serialNumber;
}

function buildAssignmentPayload(
    assetId: string,
    target: AssetAssignTarget,
): {
    readonly id: string;
    readonly assetId: string;
    readonly targetKind: "Workspace" | "User";
    readonly userId?: string;
    readonly workspaceId?: string;
} {
    return target.kind === "Workspace"
        ? {
              id: crypto.randomUUID(),
              assetId,
              targetKind: "Workspace",
              workspaceId: target.id,
          }
        : {
              id: crypto.randomUUID(),
              assetId,
              targetKind: "User",
              userId: target.id,
          };
}
